import './style.css';
import { Uppy } from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import AwsS3 from '@uppy/aws-s3';
import '@uppy/core/css/style.min.css';
import '@uppy/dashboard/css/style.min.css';
import JSZip from 'jszip';
import { initSlideshow } from './slideshow.js';

// --- State ---
let sessionToken = localStorage.getItem('wedding_gallery_token');
let currentTab = 'gallery';
let mediaList = [];
let currentPage = 1;
let hasMore = true;
let isLoading = false;
let currentFilter = 'all';

// Observer for fade-in animations
const fadeObserver = new IntersectionObserver((entries, observer) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    }
  });
}, { rootMargin: '0px 0px 50px 0px', threshold: 0.1 });

// --- DOM Elements ---
const elPinOverlay = document.getElementById('pin-overlay');
const elPinInput = document.getElementById('pin-input');
const elPinSubmit = document.getElementById('pin-submit');
const elPinError = document.getElementById('pin-error');
const elApp = document.getElementById('app');

const elGalleryGrid = document.getElementById('gallery-grid');
const elGalleryEmpty = document.getElementById('gallery-empty');
const elGalleryLoading = document.getElementById('gallery-loading');
const elLoadMoreBtn = document.getElementById('load-more');
const elNavBtns = document.querySelectorAll('.tab-btn');
const elFilterBtns = document.querySelectorAll('.filter-btn');
const elSlideshowBtn = document.getElementById('nav-slideshow');

// --- Initialization ---
function init() {
  if (sessionToken) {
    showApp();
    loadMedia();
  } else {
    elPinOverlay.classList.remove('hidden');
    elPinInput.focus();
  }
  
  setupListeners();
  if (sessionToken) {
    setupUppy();
    initSlideshow();
  }
}

// --- Auth / PIN ---
async function handlePinSubmit() {
  const pin = elPinInput.value;
  if (!pin) return;

  elPinSubmit.disabled = true;
  elPinSubmit.textContent = 'Lädt...';
  
  try {
    const res = await fetch('/api/auth', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pin })
    });
    
    if (res.ok) {
      const { token } = await res.json();
      sessionToken = token;
      localStorage.setItem('wedding_gallery_token', token);
      setupUppy();
      initSlideshow();
      showApp();
      loadMedia();
    } else {
      showPinError('Falscher Zugangscode. Bitte versuche es erneut.');
    }
  } catch (err) {
    showPinError('Netzwerkfehler. Bitte versuche es später.');
  } finally {
    elPinSubmit.disabled = false;
    elPinSubmit.innerHTML = '<span>Eintreten</span>';
    elPinInput.value = '';
  }
}

function showPinError(msg) {
  elPinError.textContent = msg;
  elPinError.classList.add('visible');
  setTimeout(() => elPinError.classList.remove('visible'), 3000);
}

function showApp() {
  elPinOverlay.classList.add('hidden');
  elApp.classList.remove('hidden');
}

function authHeaders() {
  return { 'Authorization': `Bearer ${sessionToken}` };
}

// --- Navigation ---
function switchTab(tabId) {
  currentTab = tabId;
  elNavBtns.forEach(btn => {
    btn.classList.toggle('active', btn.dataset.tab === tabId);
  });
  
  document.querySelectorAll('.section').forEach(sec => {
    if (sec.id === `section-${tabId}`) {
      sec.classList.add('section-active');
      sec.classList.remove('hidden');
    } else {
      sec.classList.remove('section-active');
      sec.classList.add('hidden');
    }
  });
  
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

// --- Video Thumbnail Generator ---
function generateVideoThumbnail(file) {
  return new Promise((resolve) => {
    let isResolved = false;
    const video = document.createElement('video');
    
    // Safety timeout: if video takes too long to load (e.g., iOS Safari blocking autoplay data), abort gracefully
    const timeout = setTimeout(() => {
      if (!isResolved) {
        isResolved = true;
        URL.revokeObjectURL(video.src);
        resolve(null);
      }
    }, 30000); // Increased to 30 seconds for large videos on mobile

    video.preload = 'metadata';
    video.playsInline = true;
    video.muted = true;
    video.src = URL.createObjectURL(file);
    
    video.onloadeddata = () => {
      video.currentTime = Math.min(1, video.duration / 2);
    };
    
    video.onseeked = () => {
      if (isResolved) return;
      
      const canvas = document.createElement('canvas');
      const scale = Math.min(1, 800 / Math.max(video.videoWidth, video.videoHeight));
      canvas.width = video.videoWidth * scale;
      canvas.height = video.videoHeight * scale;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob((blob) => {
        if (!isResolved) {
          isResolved = true;
          clearTimeout(timeout);
          URL.revokeObjectURL(video.src);
          resolve(blob);
        }
      }, 'image/webp', 0.8);
    };
    
    video.onerror = () => {
      if (!isResolved) {
        isResolved = true;
        clearTimeout(timeout);
        URL.revokeObjectURL(video.src);
        resolve(null);
      }
    };
  });
}

// --- SHA-256 Hashing (Optimized) ---
async function calculateFileHash(file) {
  const slice = file.slice(0, 1024 * 1024); // Only first 1MB
  const arrayBuffer = await slice.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  // Combine with size and lastModified for a unique but fast fingerprint
  return `${file.size}-${file.lastModified}-${hashHex}`;
}

// --- Uppy Setup ---
function setupUppy() {
  const uploaderNameInput = document.getElementById('uploader-name');
  
  const uppy = new Uppy({
    debug: false,
    autoProceed: true,
    restrictions: {
      maxFileSize: 200 * 1024 * 1024, // 200MB
      allowedFileTypes: ['image/*', 'video/*']
    },
    meta: {
      uploaderName: 'Anonym'
    },
    locale: {
      strings: {
        exceedsSize: 'Datei ist zu groß! Maximal %{size} erlaubt.',
        youCanOnlyUpload: 'Du kannst nur Bilder oder Videos hochladen.'
      }
    }
  });
  
  uppy.use(Dashboard, {
    inline: true,
    target: '#uppy-area',
    height: 60,
    showProgressDetails: true,
    showLinkIconButton: false,
    proudlyDisplayPoweredByUppy: false,
    theme: 'light',
    thumbnailGeneration: false,
    hideUploadButton: true,
    locale: {
      strings: {
        dropPasteFiles: '%{browseFiles}',
        browseFiles: 'Medien hochladen'
      }
    }
  });
  
  // Set meta on before upload
  uppy.on('file-added', (file) => {
    uppy.setFileMeta(file.id, {
      uploaderName: uploaderNameInput.value.trim() || 'Anonym'
    });
    // Expand dashboard when files are added
    const uppyWrapper = document.querySelector('.uppy-custom-wrapper');
    if (uppyWrapper) uppyWrapper.classList.add('has-files');

    // Start Video Thumbnailing in BACKGROUND (non-blocking)
    if (file.type.startsWith('video/')) {
       // Attach a promise to the file meta so we can wait for it later if needed
       const thumbPromise = generateVideoThumbnail(file.data).then(blob => {
          uppy.setFileMeta(file.id, { thumbnailBlob: blob, thumbReady: true });
          return blob;
       });
       uppy.setFileMeta(file.id, { thumbPromise });
    }
  });
  
  uploaderNameInput.addEventListener('input', (e) => {
    const name = e.target.value.trim() || 'Anonym';
    uppy.getFiles().forEach(file => {
      uppy.setFileMeta(file.id, { uploaderName: name });
    });
  });

  // Pre-process files: Generate hashes ONLY (Fast Fingerprinting)
  uppy.addPreProcessor(async (fileIDs) => {
    const promises = fileIDs.map(async (id) => {
      const file = uppy.getFile(id);
      try {
        const hash = await calculateFileHash(file.data);
        uppy.setFileMeta(id, { fileHash: hash });
      } catch (e) {
        console.error('Hashing failed', e);
      }
    });

    await Promise.all(promises);
  });
  
  uppy.use(AwsS3, {
    shouldUseMultipart: false,
    getUploadParameters (file) {
      return fetch('/api/upload/presign', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          filename: file.name,
          contentType: file.type,
          isThumbnail: false,
          fileHash: file.meta.fileHash
        })
      })
      .then(res => res.json())
      .then(data => {
        if (data.error === 'DUPLICATE_FOUND') {
          const error = new Error("Dieses Medium wurde bereits hochgeladen und wurde übersprungen.");
          error.isDuplicate = true;
          throw error;
        }

        if (data.error) {
          alert("Fehler bei Upload-Vorbereitung: " + data.error);
          throw new Error(data.error);
        }
        
        // SAVE the ID to file meta!
        uppy.setFileMeta(file.id, { id: data.id, storedName: data.storedName });

        return {
          method: data.method,
          url: data.url,
          headers: data.headers
        };
      })
      .catch(err => {
        console.error('Presign fetch failed:', err);
        alert("Upload konnte nicht vorbereitet werden: " + err.message);
        throw err;
      });
    }
  });

  uppy.on('error', (error) => {
    console.error('Uppy error:', error);
    alert('Allgemeiner Fehler: ' + error.message);
  });

  uppy.on('upload-error', (file, error, response) => {
    console.error('Upload-Fehler bei', file.name, error);
    if (error.isDuplicate) {
      // Just a notice, no scary alert needed
      console.log("Duplicate skipped:", file.name);
      return;
    }
    alert(`Fehler beim Hochladen von "${file.name}":\n${error.message || 'Die Gegenstelle (R2) hat den Upload abgebrochen. Möglicherweise ist die Datei instabil oder das WLAN abgebrochen.'}`);
  });

  uppy.on('preprocess-error', (file, error) => {
    console.error('Video-Preprocessing fehlgeschlagen:', error);
    alert(`Die Videovorschau für "${file.name}" konnte nicht erstellt werden: ${error.message}. Der Upload wird dennoch versucht.`);
  });
  
  uppy.on('postprocess-error', (file, error) => {
     alert(`Fehler nach dem Upload von "${file.name}": ${error.message}`);
  });

  uppy.on('upload', () => {
    // Started
  });

  const pendingFinalizations = [];

  uppy.on('upload-success', (file, response) => {
    const finalizePromise = (async () => {
      const id = file.meta.id || file.name.split('.')[0]; // Fallback
      const storedName = file.meta.storedName || `${id}.${file.extension}`;
      const mediaType = file.type.startsWith('image/') ? 'image' : 'video';
      
      let hasThumbnail = false;
      let thumbnailBlob = file.meta.thumbnailBlob;

      // Finalize: Save to D1
      try {
        let hasThumbnail = false;
        // If it's a video, check IF thumbnail is ready, but DON'T block if it's taking too long
        if (mediaType === 'video' && !thumbnailBlob && file.meta.thumbPromise) {
           const timeoutPromise = new Promise(resolve => setTimeout(() => resolve(null), 2000));
           thumbnailBlob = await Promise.race([file.meta.thumbPromise, timeoutPromise]);
        }

        // Only try to upload thumbnail if we actually have one
        if (mediaType === 'video' && thumbnailBlob) {
          try {
            const preRes = await fetch('/api/upload/presign', {
              method: 'POST',
              headers: authHeaders(),
              body: JSON.stringify({
                filename: 'thumb.webp',
                isThumbnail: true,
                mainId: file.meta.id
              })
            });
            const preData = await preRes.json();
            await fetch(preData.url, {
              method: 'PUT',
              headers: { 'Content-Type': 'image/webp' },
              body: thumbnailBlob
            });
            hasThumbnail = true;
          } catch (e) {
            console.warn('Background thumbnail upload failed', e);
          }
        }

        await fetch('/api/upload/finalize', {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            id: file.meta.id,
            original_name: file.name,
            stored_name: file.meta.storedName,
            mime_type: file.type,
            file_size: file.size,
            media_type: mediaType,
            uploader_name: file.meta.uploaderName || 'Anonym',
            has_thumbnail: hasThumbnail,
            fileHash: file.meta.fileHash
          })
        });
      } catch (err) {
        console.error('Finalize failed:', err);
      }
    })();
    
    pendingFinalizations.push(finalizePromise);
  });

  window.uppy = uppy; // Expose for debugging if needed

  uppy.on('complete', async (result) => {
    console.log('Upload complete result:', result);
    const hasSuccessful = result.successful.length > 0;
    
    try {
      // 1. Wait briefly for any PENDING finalizations, but don't hang if they are stuck
      if (pendingFinalizations.length > 0) {
        // Race against a 3s safety timeout for the WHOLE completion logic
        const finalizeComplete = Promise.allSettled(pendingFinalizations);
        const safetyTimeout = new Promise(resolve => setTimeout(resolve, 3000));
        await Promise.race([finalizeComplete, safetyTimeout]);
        pendingFinalizations.length = 0; // Clear array
      }

      // 2. CELEBRATION! Confetti blast if anything succeeded
      if (hasSuccessful && typeof confetti === 'function') {
        const count = 150; // slightly fewer for mobile performance
        const defaults = {
          origin: { y: 0.7 },
          colors: ['#bc9c22', '#ffffff', '#e0e0e0', '#d4af37'],
          zIndex: 10000
        };

        function fire(particleRatio, opts) {
          confetti({
            ...defaults,
            ...opts,
            particleCount: Math.floor(count * particleRatio)
          });
        }

        fire(0.25, { spread: 26, startVelocity: 55 });
        fire(0.2, { spread: 60 });
        fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
        fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
      }
    } catch (err) {
      console.error('Error in complete handler:', err);
    } finally {
      // 3. ALWAYS reset after 5 seconds to provide a clean slate
      setTimeout(() => {
        // Reset Uppy state
        try {
          uppy.cancelAll();
        } catch (e) {
          console.error("cancelAll failed", e);
        }
        
        // These must ALWAYS run, even if cancelAll fails
        const uppyWrapper = document.querySelector('.uppy-custom-wrapper');
        if (uppyWrapper) uppyWrapper.classList.remove('has-files');
        
        loadMedia(true); 
        setTimeout(() => switchTab('gallery'), 100);
      }, 5000);
    }
  });

  uppy.on('cancel-all', () => {
    const uppyWrapper = document.querySelector('.uppy-custom-wrapper');
    if (uppyWrapper) uppyWrapper.classList.remove('has-files');
  });
}

// --- Gallery Logic ---
async function loadMedia(reset = false) {
  if (isLoading) return;
  if (reset) {
    currentPage = 1;
    mediaList = [];
    elGalleryGrid.innerHTML = '';
    hasMore = true;
  }
  
  if (!hasMore) return;
  
  isLoading = true;
  elGalleryLoading.classList.remove('hidden');
  elLoadMoreBtn.classList.add('hidden');
  elGalleryEmpty.classList.add('hidden');
  
  try {
    let url = `/api/media?page=${currentPage}&limit=24`;
    if (currentFilter !== 'all') {
      url += `&type=${currentFilter}`;
    }
    
    const res = await fetch(url, { headers: authHeaders() });
    if (res.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('wedding_gallery_token');
      sessionToken = null;
      elPinOverlay.classList.remove('hidden');
      elApp.classList.add('hidden');
      return;
    }
    
    const data = await res.json();
    
    hasMore = data.hasMore;
    
    if (data.media.length === 0 && currentPage === 1) {
      elGalleryEmpty.classList.remove('hidden');
    } else {
      data.media.forEach(appendMediaItem);
      mediaList.push(...data.media);
      currentPage++;
    }
  } catch (err) {
    console.error('Failed to load media:', err);
  } finally {
    isLoading = false;
    elGalleryLoading.classList.add('hidden');
    if (hasMore) {
      elLoadMoreBtn.classList.remove('hidden');
    }
    // Show download-all button when there's media in the gallery
    const dlBtn = document.getElementById('download-all');
    if (dlBtn && mediaList.length > 0) {
      dlBtn.classList.remove('hidden');
    }
  }
}

function appendMediaItem(item) {
  const div = document.createElement('div');
  div.className = 'gallery-item gallery-fade-in';
  div.dataset.id = item.id;
  div.onclick = () => openLightbox(item);
  
  let mediaElement;
  if (item.media_type === 'video' && !item.thumbnailUrl) {
    mediaElement = document.createElement('video');
    mediaElement.src = window.getMediaUrl(item.url) + '#t=0.1';
    mediaElement.preload = 'metadata';
    mediaElement.muted = true;
    mediaElement.playsInline = true;
    mediaElement.className = 'gallery-video-fallback';
  } else {
    mediaElement = document.createElement('img');
    mediaElement.loading = 'lazy';
    mediaElement.src = window.getMediaUrl(item.thumbnailUrl || item.url);
  }
  
  div.appendChild(mediaElement);
  
  if (item.media_type === 'video') {
    const icon = document.createElement('div');
    icon.className = 'gallery-item-video-icon';
    icon.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="5 3 19 12 5 21 5 3"/></svg>';
    div.appendChild(icon);
  }
  
  elGalleryGrid.appendChild(div);
  fadeObserver.observe(div);
}

// Helper to fetch protected media
window.getMediaUrl = function(url) {
  if (!url) return '';
  const token = encodeURIComponent(sessionToken);
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}token=${token}`;
};

window.getAuthToken = () => sessionToken;

// --- Lightbox ---
const elLightbox = document.getElementById('lightbox');
const elLbImage = document.getElementById('lightbox-image');
const elLbVideo = document.getElementById('lightbox-video');
const elLbUploader = document.getElementById('lightbox-uploader');
const elLbDate = document.getElementById('lightbox-date');
const elLbDownload = document.getElementById('lightbox-download');

let currentLightboxIndex = -1;

function openLightbox(item) {
  currentLightboxIndex = mediaList.findIndex(m => m.id === item.id);
  updateLightboxContent(item);
  elLightbox.classList.remove('hidden');
}

function updateLightboxContent(item) {
  elLbUploader.textContent = `Von: ${item.uploader_name}`;
  elLbDate.textContent = new Date(item.created_at).toLocaleDateString('de-DE');
  
  elLbDownload.onclick = async (e) => {
    e.preventDefault();
    const res = await fetch(`${item.url}?download=1`, { headers: authHeaders() });
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = item.original_name;
    a.click();
  };

  if (item.media_type === 'image') {
    elLbVideo.classList.add('hidden');
    elLbVideo.pause();
    elLbImage.classList.remove('hidden');
    elLbImage.src = window.getMediaUrl(item.url);
  } else {
    elLbImage.classList.add('hidden');
    elLbVideo.classList.remove('hidden');
    elLbVideo.src = window.getMediaUrl(item.url);
    elLbVideo.play().catch(() => console.log("Autoplay blocked"));
  }
}

function closeLightbox() {
  elLightbox.classList.add('hidden');
  elLbVideo.pause();
  elLbVideo.src = '';
  elLbImage.src = '';
  if (window.resetLightboxZoom) window.resetLightboxZoom();
}

function nextLightbox() {
  if (window.resetLightboxZoom) window.resetLightboxZoom();
  if (currentLightboxIndex < mediaList.length - 1) {
    currentLightboxIndex++;
    updateLightboxContent(mediaList[currentLightboxIndex]);
  } else if (hasMore) {
    loadMedia().then(() => nextLightbox());
  }
}

function prevLightbox() {
  if (window.resetLightboxZoom) window.resetLightboxZoom();
  if (currentLightboxIndex > 0) {
    currentLightboxIndex--;
    updateLightboxContent(mediaList[currentLightboxIndex]);
  }
}

async function handleMediaDelete() {
  if (currentLightboxIndex === -1) return;
  const item = mediaList[currentLightboxIndex];
  
  const pin = prompt("Admin-Passwort zum Löschen eingeben:");
  if (pin === null) return; // User cancelled

  if (!confirm("Bist du sicher, dass du dieses Medium unwiderruflich löschen möchtest?")) return;

  try {
    const res = await fetch(`/api/media/${item.id}`, {
      method: 'DELETE',
      headers: {
        ...authHeaders(),
        'X-Admin-PIN': pin
      }
    });

    if (res.ok) {
      alert("Erfolgreich gelöscht.");
      // Remove from UI and list
      mediaList.splice(currentLightboxIndex, 1);
      const thumb = document.querySelector(`.gallery-item[data-id="${item.id}"]`);
      if (thumb) thumb.remove();
      
      if (mediaList.length > 0) {
        // Show next or prev
        if (currentLightboxIndex >= mediaList.length) currentLightboxIndex--;
        updateLightboxContent(mediaList[currentLightboxIndex]);
      } else {
        closeLightbox();
        elGalleryEmpty.classList.remove('hidden');
      }
    } else {
      const err = await res.json();
      alert(`Fehler: ${err.error || 'Löschen fehlgeschlagen'}`);
    }
  } catch (e) {
    alert("Netzwerkfehler beim Löschen.");
  }
}

// --- Listeners ---
function setupPinchToZoom() {
  let currentScale = 1;
  let initialDistance = 0;
  
  const elLightboxWrap = document.getElementById('lightbox-content');
  if (!elLightboxWrap) return;

  elLightboxWrap.addEventListener('touchstart', (e) => {
    if (e.touches.length === 2 && !elLbImage.classList.contains('hidden')) {
      e.preventDefault();
      initialDistance = Math.hypot(
        e.touches[0].pageX - e.touches[1].pageX,
        e.touches[0].pageY - e.touches[1].pageY
      );
    }
  }, { passive: false });
  
  elLightboxWrap.addEventListener('touchmove', (e) => {
    if (e.touches.length === 2 && !elLbImage.classList.contains('hidden')) {
      e.preventDefault();
      const currentDistance = Math.hypot(
        e.touches[0].pageX - e.touches[1].pageX,
        e.touches[0].pageY - e.touches[1].pageY
      );
      
      const newScale = currentScale * (currentDistance / initialDistance);
      // Limits 1x to 4x zoom
      currentScale = Math.min(Math.max(1, newScale), 4);
      initialDistance = currentDistance;
      
      elLbImage.style.transform = `scale(${currentScale})`;
      elLbImage.style.transition = 'none';
    }
  }, { passive: false });
  
  elLightboxWrap.addEventListener('touchend', (e) => {
    if (e.touches.length < 2 && currentScale > 1) {
      // Snap back to original size when fingers are released
      currentScale = 1;
      elLbImage.style.transition = 'transform 0.3s ease';
      elLbImage.style.transform = `scale(1)`;
    }
  });

  // Reset zoom on navigation helper
  window.resetLightboxZoom = () => {
    if(currentScale !== 1) {
       currentScale = 1;
       elLbImage.style.transition = 'transform 0.3s ease';
       elLbImage.style.transform = `scale(1)`;
    }
  };
}

function setupListeners() {
  setupPinchToZoom();
  elPinSubmit.addEventListener('click', handlePinSubmit);
  elPinInput.addEventListener('keypress', (e) => {
    if (e.key === 'Enter') handlePinSubmit();
  });
  
  elNavBtns.forEach(btn => {
    btn.addEventListener('click', () => switchTab(btn.dataset.tab));
  });

  if (elSlideshowBtn) {
    elSlideshowBtn.addEventListener('click', () => switchTab('slideshow'));
  }
  
  document.getElementById('go-to-upload').addEventListener('click', () => switchTab('upload'));
  
  elFilterBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      elFilterBtns.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      currentFilter = btn.dataset.filter;
      loadMedia(true);
    });
  });
  
  elLoadMoreBtn.addEventListener('click', () => loadMedia());

  // Download All
  const dlAllBtn = document.getElementById('download-all');
  const dlProgress = document.getElementById('download-progress');
  const dlProgressBar = document.getElementById('download-progress-bar');
  const dlProgressText = document.getElementById('download-progress-text');
  const dlCancel = document.getElementById('download-cancel');
  let dlAbortController = null;

  if (dlAllBtn) {
    dlAllBtn.addEventListener('click', async () => {
      await handleDownloadAll(dlProgress, dlProgressBar, dlProgressText);
    });
  }
  if (dlCancel) {
    dlCancel.addEventListener('click', () => {
      if (dlAbortController) dlAbortController.abort();
      dlProgress.classList.add('hidden');
    });
  }

  async function handleDownloadAll(progressEl, barEl, textEl) {
    // 1. Fetch ALL media metadata
    try {
      const res = await fetch('/api/media?page=1&limit=9999', { headers: authHeaders() });
      const data = await res.json();
      const allMedia = data.media;

      if (!allMedia || allMedia.length === 0) {
        alert('Keine Medien zum Herunterladen vorhanden.');
        return;
      }

      // 2. Show progress overlay
      progressEl.classList.remove('hidden');
      barEl.style.width = '0%';
      textEl.textContent = `0 / ${allMedia.length} Dateien`;

      dlAbortController = new AbortController();
      const signal = dlAbortController.signal;

      // 3. Create ZIP and download files
      const zip = new JSZip();
      let completed = 0;

      for (const item of allMedia) {
        if (signal.aborted) break;

        try {
          const fileRes = await fetch(item.url, { headers: authHeaders(), signal });
          const blob = await fileRes.blob();
          const ext = item.original_name ? item.original_name.split('.').pop() : (item.mime_type.split('/')[1] || 'bin');
          const filename = item.original_name || `${item.id}.${ext}`;
          zip.file(filename, blob);
        } catch (e) {
          if (signal.aborted) break;
          console.warn(`Skipping ${item.id}:`, e);
        }

        completed++;
        const pct = Math.round((completed / allMedia.length) * 100);
        barEl.style.width = `${pct}%`;
        textEl.textContent = `${completed} / ${allMedia.length} Dateien`;
      }

      if (signal.aborted) return;

      // 4. Generate ZIP and trigger download
      textEl.textContent = 'ZIP wird erstellt...';
      barEl.style.width = '100%';

      const zipBlob = await zip.generateAsync({ type: 'blob' }, (metadata) => {
        textEl.textContent = `ZIP wird erstellt... ${Math.round(metadata.percent)}%`;
      });

      const a = document.createElement('a');
      a.href = URL.createObjectURL(zipBlob);
      a.download = 'Hochzeit_Lisa_Markus_Medien.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);

    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Download failed:', err);
        alert('Der Download ist fehlgeschlagen. Bitte versuche es erneut.');
      }
    } finally {
      progressEl.classList.add('hidden');
      dlAbortController = null;
    }
  }
  
  // Lightbox
  document.getElementById('lightbox-close').addEventListener('click', closeLightbox);
  document.getElementById('lightbox-next').addEventListener('click', nextLightbox);
  document.getElementById('lightbox-prev').addEventListener('click', prevLightbox);
  document.getElementById('lightbox-delete').addEventListener('click', handleMediaDelete);
  
  document.addEventListener('keydown', (e) => {
    if (elLightbox.classList.contains('hidden')) return;
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowRight') nextLightbox();
    if (e.key === 'ArrowLeft') prevLightbox();
    if (e.key === 'Delete') handleMediaDelete();
  });

  // Scroll to Top feature
  const btnScrollTop = document.getElementById('btn-scroll-top');
  if (btnScrollTop) {
    window.addEventListener('scroll', () => {
      // Toggle button visibility based on scroll position
      if (window.scrollY > 400) {
        btnScrollTop.classList.remove('hidden');
      } else {
        btnScrollTop.classList.add('hidden');
      }
    });

    btnScrollTop.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }
}

// Start
init();
